import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Injector,
    inject,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { IChecklist2Parameter, IChecklistV2ForList } from '@fi-shared/checklist/checklist-v2';
import { ObjectTypes } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { IItemsListAction2, IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiChecklist2AuditService } from '@fi-shared/checklist/fi-checklist2-audit/fi-checklist2-audit.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiChecklist2ListBase } from '@fi-shared/checklist/fi-checklist2-list/fi-checklist2-list-base';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { FiChecklist2ReportStateService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-state.service';

@Component({
    selector: 'fi-checklist2-audit-list-table-per-unit',
    templateUrl: './fi-checklist2-audit-list-table-per-unit.component.html',
    styleUrls: ['./fi-checklist2-audit-list-table-per-unit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-audit-list-table-per-unit' },
    standalone: false,
})
export class FiChecklist2AuditListTablePerUnitComponent extends FiChecklist2ListBase implements OnInit {
    private fiChecklist2AuditService = inject(FiChecklist2AuditService);
    private detectDeviceService = inject(DetectDevicesService);
    private fiChecklistStoreService = inject(FiChecklistStoreService);
    private fiChecklist2ReportStateService = inject(FiChecklist2ReportStateService);

    public readonly defaultSortColumnName = 'DueDateTime';
    public readonly defaultSortDirection = false;
    public readonly objectType = ObjectTypes.CheckList;
    public readonly itemsListType = FiItemsListType.ChecklistV2AuditsPerUnit;
    public actions: IItemsListAction2<IChecklistV2ForList>[];

    public readonly auditMode: boolean = true;

    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.actions = this.getChecklistAuditListActions();

        this.subscribeOnChecklistAnswersChangeForList({
            loadChecklistsFn: (checklistToScheduleIds) => {
                return new Promise<IChecklistV2ForList[]>((resolve, reject) => {
                    const parameter: IChecklist2Parameter = {
                        FetchParams: {
                            FetchItems: true,
                            FetchTotalCount: false,
                            FetchLightWeightObjects: false,
                        },
                        ChecklistToScheduleIds: checklistToScheduleIds,
                        AllUnitsMode: true,
                        OnlyWithPeriods: true,
                        AuditMode: true,
                    };
                    this.fiChecklist2AuditService
                        .getChecklistAuditListData(parameter)
                        .then((listData) => resolve(listData.Items), reject);
                });
            },
        });
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IChecklistV2ForList>> {
        return new Promise<IItemsListDataFetchResult<IChecklistV2ForList>>((resolve, reject) => {
            const parameter = this.getItemsListParameter(loadArgs) as IChecklist2Parameter;
            parameter.AllUnitsMode = true;
            parameter.OnlyWithPeriods = true;
            parameter.AuditMode = true;
            if (loadArgs.fetchOnlyItems) {
                // we don't need timeout on sorting
                this.fiChecklist2AuditService.getChecklistAuditListData(parameter).then(resolve, reject);
            } else {
                setTimeout(() => {
                    // wait a while for preventing server abusing
                    this.fiChecklist2AuditService.getChecklistAuditListData(parameter).then(resolve, reject);
                }, 500);
            }
        });
    }

    private getChecklistAuditListActions(): IItemsListAction2<IChecklistV2ForList>[] {
        const actions: IItemsListAction2<IChecklistV2ForList>[] = [
            this.fiChecklistActionsHelperService.getChecklistEditAction<IChecklistV2ForList>({
                checklistIdFn: (data) => data.Id,
                auditMode: this.auditMode,
                templateMode: false,
            }),
            this.fiChecklistActionsHelperService.getViewReportAction<IChecklistV2ForList>({
                onClick: (data) => {
                    this.fiChecklist2ReportStateService.goToReport(data.Id, data.ScheduleId, data, this.auditMode);
                },
                businessUnitId: this.userDataService.getUserBusinessUnitId(),
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getChecklistGenerateTemplateAction({
                checklistIdFn: (data) => data.Id,
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getChecklistGenerateAuditChecklistAction({
                checklistIdFn: (data) => data.Id,
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getDeleteChecklistResponsesAction<IChecklistV2ForList>({
                getChecklistToScheduleIdFn: (data) => data.ChecklistToScheduleId,
                getChecklistNameFn: (data) => data.Name,
                getScheduleNameFn: (data) => data.ScheduleName,
                disabled: (data) => !data.HasAnswers,
                afterDeleteFn: (data) => {
                    this.reloadData();
                    this.showResultSnackbar(this.appTextService.get('checklist.responses_deleted'));
                },
                businessUnitId: this.userDataService.getUserBusinessUnitId(),
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getChecklistUnitNotApplicableAction({
                hasAnswersFn: (data) => data.FilledCheckpointsCount > 0,
                getUnitNameFn: (data) => data.BusinessUnitName,
                getChecklistNameFn: (data) => data.Name,
                onSuccess: (data) => {
                    data.FilledCheckpointsCount = 0;
                    this.fiChecklistStoreService.triggerChecklistListDataChanged(data);
                },
                businessUnitId: this.userDataService.getUserBusinessUnitId(),
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getDeleteChecklistAction<IChecklistV2ForList>({
                onClick: (data) => {
                    this.fiChecklistHelperService
                        .getConfirmDeleteChecklistDialog({
                            item: data,
                            checklistIdFn: () => data.Id,
                            auditMode: this.auditMode,
                        })
                        .then((result) => {
                            if (result.result) {
                                this.fiChecklist2Service.deleteChecklists([data.Id], this.auditMode).then(() => {
                                    const objectType = !this.auditMode
                                        ? ObjectTypes.Checklist2
                                        : ObjectTypes.Checklist2Audit;
                                    this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                                        objectType,
                                        [data.Id],
                                        data.Name,
                                    );

                                    this.removeData([data]); // for excluding deleted checklist from list
                                });
                            }
                        });
                },
                auditMode: this.auditMode,
                templateMode: false,
            }),
        ];
        return actions;
    }
}
